// src/pages/account/VerifyEmailPage.js
import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { UserContext } from '../../context/UserContext'; // Import UserContext
import { NotificationContext } from '../../context/NotificationContext'; // Import NotificationContext
import { apiFetch } from '../../utils/apiClient'; // Import apiFetch
import { BASE_URL } from '../../utils/config';
import { useLanguage } from '../../hooks/useLanguage';
import './VerifyEmailPage.css'; // We'll create this CSS file next

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState(t('verifyEmailPage.verifying'));
  const [isLoading, setIsLoading] = useState(true);
  const { login } = useContext(UserContext); // Get user context functions
  const { addNotification } = useContext(NotificationContext); // Get notification context function
  const lang = useLanguage();

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setVerificationStatus(t('verifyEmailPage.invalidLink'));
      setIsLoading(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await apiFetch(
          `${BASE_URL}/authUser/confirm-email-verification`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          },
          null // No user token is needed for this operation
        );

        const data = await response.json();

        if (response.ok) {
          // NEW: Instead of redirecting to login, we log the user in directly.
          // This ensures the app state is immediately updated with the verified status.
          addNotification(t('verifyEmailPage.success'), 'success');
          // Use the centralized login function from the context
          login(data);

          // 4. Redirect based on verification status.
          // If the phone is already verified, the user is now fully verified. Redirect to home.
          if (data.user.isPhoneVerified) {
            navigate(`/${lang}`);
          } else {
            // Otherwise, redirect to preferences to encourage phone verification.
            // We pass a state flag to let the preferences page know the user just
            // verified their email, so it can display a welcome/guidance message.
            navigate(`/${lang}/preferences`, { state: { from: 'email-verification' } });
          }
        } else {
          // Handle specific error messages from backend
          setVerificationStatus(data.message || t('verifyEmailPage.failed'));
        }
      } catch (error) {
        console.error('Verification API error:', error);
        setVerificationStatus(t('verifyEmailPage.unexpectedError'));
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate, login, addNotification, t, lang]); // Add context functions to dependency array

  return (
    <div className="verify-email-page-container">
      <div className="verify-email-content">
        <h2>{t('verifyEmailPage.title')}</h2>
        {isLoading ? (
          <p className="status-loading">{verificationStatus}</p>
        ) : (
          // If not loading, it means there was an error, as success causes a redirect.
          <>
            <p className="status-error">{verificationStatus}</p>
            <div className="verify-actions">
              <Link to={`/${lang}/login`} className="verify-link-button">
                {t('verifyEmailPage.goToLogin')}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
